import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "countrySelectAllCheckbox",
    "platformCheckbox",
    "appCheckbox",
    "errorPanel",
    "errorMessage",
    "confirmationPanel",
    "confirmationSummary",
    "confirmButton"
  ]

  connect() {
    this._onChange = () => this._hideConfirmIfVisible();
    this.element.addEventListener("change", this._onChange);
  }

  disconnect() {
    this.element.removeEventListener("change", this._onChange);
  }

  _hideConfirmIfVisible() {
    if (this.hasConfirmationPanelTarget && !this.confirmationPanelTarget.hidden) {
      this.hideConfirmationPanel();
    }
  }

  toggleAllWhiteLabels(event) {
    const checked = event.target.checked;

    this.appCheckboxTargets.forEach((app) => {
      app.checked = checked;
    });

    if (this.hasCountrySelectAllCheckboxTarget) {
      this.countrySelectAllCheckboxTargets.forEach((country) => (country.checked = checked));
    }

    this.hideError();
  }

  toggleCountry(event) {
    const country = event.target.dataset.country;
    const checked = event.target.checked;

    this.appCheckboxTargets
      .filter((app) => app.dataset.country === country)
      .forEach((app) => (app.checked = checked));

    this.hideError();
  }

  toggleAllApps(event) {
    const checked = event.target.checked;

    this.appCheckboxTargets.forEach((app) => {
      app.checked = checked;
    });

    this.hideError();
  }

  validate(event) {
    if (event) event.preventDefault();

    this.hideError();

    const platformValid = this.platformCheckboxTargets.some((platform) => platform.checked);
    const appValid = this.appCheckboxTargets.some((app) => app.checked);

    if (!platformValid || !appValid) {
      const errors = [];

      if (!platformValid) errors.push("at least one platform (iOS or Android)");
      if (!appValid) errors.push("at least one app");

      this.showError(`Please select ${errors.join(" and ")}.`);
      return;
    }

    this.showConfirmationPanel();
  }

  selectedPlatforms() {
    return this.platformCheckboxTargets
      .filter((platform) => platform.checked)
      .map((platform) => platform.dataset.label || platform.value);
  }

  selectedApps() {
    return this.appCheckboxTargets
      .filter((app) => app.checked)
      .map((app) => app.dataset.label || app.value);
  }

  showConfirmationPanel() {
    const platforms = this.selectedPlatforms();
    const apps = this.selectedApps();

    const costSummary = this.estimatedCostSummary();

    this.confirmationSummaryTarget.replaceChildren(
      this.summaryLine("Platforms:", platforms.join(", ") || "None"),
      this.summaryLine("Apps:", apps.join(", ") || "None"),
      this.summaryLine("Estimated Cost:", costSummary)
    );

    this.confirmationPanelTarget.hidden = false;

    this.confirmationPanelTarget.scrollIntoView({
      behavior: "smooth",
      block: "center"
    });
  }

  hideConfirmationPanel(event) {
    if (event) event.preventDefault();
    if (!this.hasConfirmationPanelTarget) return;

    this.confirmationPanelTarget.hidden = true;
    if (this.hasConfirmationSummaryTarget) this.confirmationSummaryTarget.replaceChildren();
  }

  summaryLine(label, value) {
    const div = document.createElement("div");
    const strong = document.createElement("strong");
    strong.textContent = label;
    div.appendChild(strong);
    div.append(` ${value}`);
    return div;
  }

  estimatedCostSummary() {
    const appCount = this.selectedAppCount();
    const { hasIOS, hasAndroid } = this.selectedPlatformFlags();

    const iosDeployments = hasIOS ? appCount : 0;
    const androidDeployments = hasAndroid ? appCount : 0;

    const IOS_COST_PER_DEPLOYMENT = 0.75;
    const ANDROID_COST_PER_DEPLOYMENT = 0.15;

    const iosCost = iosDeployments * IOS_COST_PER_DEPLOYMENT;
    const androidCost = androidDeployments * ANDROID_COST_PER_DEPLOYMENT;
    const total = iosCost + androidCost;

    if (!hasIOS && !hasAndroid) return "$0.00";
    if (appCount === 0) return "$0.00";

    return `~$${total.toFixed(2)}`;
  }

  selectedAppCount() {
    return this.appCheckboxTargets.filter((a) => a.checked).length;
  }

  selectedPlatformFlags() {
    const selected = this.platformCheckboxTargets.filter((platform) => platform.checked);
    const values = selected.map((selectedPlatform) => (selectedPlatform.value || "").toLowerCase());

    return {
      hasIOS: values.includes("ios"),
      hasAndroid: values.includes("android")
    };
  }

  showError(message) {
    if (!this.hasErrorPanelTarget || !this.hasErrorMessageTarget) return;

    this.errorMessageTarget.textContent = message;
    this.errorPanelTarget.hidden = false;
  }

  hideError() {
    if (!this.hasErrorPanelTarget || !this.hasErrorMessageTarget) return;

    this.errorPanelTarget.hidden = true;
    this.errorMessageTarget.textContent = "";
  }
}
